import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { TrendingUp, Users, Car, DollarSign, TicketCheck, Wallet, UserPlus, AlertTriangle } from "lucide-react";

const COLORS = ["#1B4332", "#D4A017", "#2D6A4F", "#40916C", "#52B788", "#74C69D", "#95D5B2"];

export default function AnalyticsDashboard({ token }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState({});
  const [rideTrends, setRideTrends] = useState([]);
  const [serviceBreakdown, setServiceBreakdown] = useState([]);
  const [userGrowth, setUserGrowth] = useState([]);
  const [driverPerformance, setDriverPerformance] = useState([]);
  const [revenueByMethod, setRevenueByMethod] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [ov, rt, sb, ug, dp, rm] = await Promise.all([
        fetch(`${API}/admin/analytics/overview`, { headers }).then(r => r.json()),
        fetch(`${API}/admin/analytics/ride-trends?days=30`, { headers }).then(r => r.json()),
        fetch(`${API}/admin/analytics/service-breakdown`, { headers }).then(r => r.json()),
        fetch(`${API}/admin/analytics/user-growth?days=30`, { headers }).then(r => r.json()),
        fetch(`${API}/admin/analytics/driver-performance`, { headers }).then(r => r.json()),
        fetch(`${API}/admin/analytics/revenue-by-method`, { headers }).then(r => r.json()),
      ]);
      setOverview(ov);
      setRideTrends(rt);
      setServiceBreakdown(sb);
      setUserGrowth(ug);
      setDriverPerformance(dp.slice(0, 10));
      setRevenueByMethod(rm);
    } catch (err) {
      console.error("Failed to load analytics:", err);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-forest border-t-transparent rounded-full" />
      </div>
    );
  }

  const statCards = [
    { label: t("totalRides"), value: overview.total_rides || 0, icon: Car, color: "bg-forest" },
    { label: t("completedRides"), value: overview.completed_rides || 0, icon: TrendingUp, color: "bg-green-600" },
    { label: t("cancelledRides"), value: overview.cancelled_rides || 0, icon: AlertTriangle, color: "bg-red-500" },
    { label: t("totalRevenue"), value: `₹${(overview.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: "bg-amber" },
    { label: t("totalUsers"), value: overview.total_users || 0, icon: Users, color: "bg-blue-600" },
    { label: t("totalDrivers"), value: overview.total_drivers || 0, icon: Car, color: "bg-slate-700" },
    { label: t("openTickets"), value: overview.open_tickets || 0, icon: TicketCheck, color: "bg-orange-500" },
    { label: t("totalWalletBalance"), value: `₹${(overview.total_wallet_balance || 0).toLocaleString()}`, icon: Wallet, color: "bg-purple-600" },
  ];

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <div key={i} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 ${stat.color} rounded-xl flex items-center justify-center`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
            <p className="text-sm text-slate-500">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ride Trends */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <h3 className="font-semibold text-lg text-slate-900 mb-4">{t("rideAnalytics")} (30 Days)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={rideTrends}>
              <defs>
                <linearGradient id="colorRides" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1B4332" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#1B4332" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#D4A017" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#D4A017" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip 
                contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}
                labelFormatter={(v) => `Date: ${v}`}
              />
              <Legend />
              <Area type="monotone" dataKey="rides" stroke="#1B4332" fill="url(#colorRides)" name="Total Rides" />
              <Area type="monotone" dataKey="completed" stroke="#40916C" fill="url(#colorRevenue)" name="Completed" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Trend */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <h3 className="font-semibold text-lg text-slate-900 mb-4">{t("revenueChart")}</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={rideTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${v}`} />
              <Tooltip 
                contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}
                formatter={(v) => [`₹${v.toLocaleString()}`, "Revenue"]}
              />
              <Line type="monotone" dataKey="revenue" stroke="#D4A017" strokeWidth={3} dot={{ fill: "#D4A017", r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Service Breakdown */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <h3 className="font-semibold text-lg text-slate-900 mb-4">{t("serviceBreakdown")}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={serviceBreakdown}
                dataKey="count"
                nameKey="service"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ service, percent }) => `${service} ${(percent * 100).toFixed(0)}%`}
                labelLine={{ stroke: "#666", strokeWidth: 1 }}
              >
                {serviceBreakdown.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v, name) => [v, name]} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* User Growth */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <h3 className="font-semibold text-lg text-slate-900 mb-4">{t("userGrowth")}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={userGrowth}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(8)} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip 
                contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}
              />
              <Bar dataKey="signups" fill="#1B4332" radius={[4, 4, 0, 0]} name={t("signups")} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue by Payment Method */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <h3 className="font-semibold text-lg text-slate-900 mb-4">Revenue by Payment</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={revenueByMethod}
                dataKey="total"
                nameKey="method"
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                label={({ method }) => method}
              >
                {revenueByMethod.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[(index + 2) % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => [`₹${v.toLocaleString()}`, "Revenue"]} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Driver Performance Table */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <h3 className="font-semibold text-lg text-slate-900 mb-4">{t("driverPerformance")} (Top 10)</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-3 px-4 font-medium text-slate-500">#</th>
                <th className="text-left py-3 px-4 font-medium text-slate-500">Driver</th>
                <th className="text-right py-3 px-4 font-medium text-slate-500">Rides</th>
                <th className="text-right py-3 px-4 font-medium text-slate-500">Earnings</th>
                <th className="text-right py-3 px-4 font-medium text-slate-500">Rating</th>
                <th className="text-center py-3 px-4 font-medium text-slate-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {driverPerformance.map((driver, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="py-3 px-4 text-slate-600">{i + 1}</td>
                  <td className="py-3 px-4 font-medium text-slate-900">{driver.name || "Unknown"}</td>
                  <td className="py-3 px-4 text-right text-slate-600">{driver.completed_rides}</td>
                  <td className="py-3 px-4 text-right text-forest font-medium">₹{driver.earnings?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-amber font-medium">{driver.rating?.toFixed(1)} ★</td>
                  <td className="py-3 px-4 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      driver.status === "online" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"
                    }`}>
                      {driver.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
